import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useUserWebSocket } from "@/lib/useUserWebSocket";
import {
  BroadcastIcon, UsersThreeIcon, RobotIcon, StopCircleIcon, LightningIcon,
} from "@phosphor-icons/react";

/**
 * Admin → Live Boost console.
 *
 * Two knobs per live stream:
 *   1. Inject viewer count — bumps the stream's viewer_count instantly.
 *      Broadcasts a presence event so every connected viewer's LIVE pill
 *      updates in realtime (no polling). Optional gradual drain-back for
 *      natural feel.
 *   2. Realistic chat bots — spawns N asyncio bots that drop LLM-written
 *      short chat lines every ~N seconds. Bots inherit the stream's
 *      title + category context so lines feel on-topic.
 *
 * The live-streams list refreshes every 4 s while this page is mounted
 * (short poll — no separate admin channel plumbing needed).
 */
export default function AdminBoost() {
  const { user } = useAuth();
  const [streams, setStreams] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/admin/streams/live");
      setStreams(r.data.items || []);
    } catch (e) {
      // Only toast if this looks like a real auth/perm issue —
      // transient network hiccups shouldn't spam the admin.
      const status = e?.response?.status;
      if (status === 401 || status === 403) toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Persona catalog only loads once; it never changes at runtime.
    api.get("/admin/bot-personas")
      .then((r) => setPersonas(r.data.personas || []))
      .catch(() => setPersonas([]));
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [refresh]);

  // Optional: react to stream-lifecycle WS events (a seller going live)
  // by kicking a fresh refresh so the picker never lags behind reality.
  useUserWebSocket({
    enabled: !!user && user.role === "admin",
    onEvent: (msg) => {
      if (msg?.type === "stream.started" || msg?.type === "stream.ended") refresh();
    },
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-10 text-zinc-500">Loading live shows…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="admin-boost-page">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono mb-1">
              Growth Ops
            </div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter inline-flex items-center gap-2">
              Live Boost
              <LightningIcon size={28} weight="fill" className="text-amber-500" />
            </h1>
          </div>
          <Link
            to="/admin"
            className="text-sm text-[#1D78AB] hover:underline"
            data-testid="admin-boost-back"
          >
            ← Back to Admin
          </Link>
        </div>
        <p className="text-zinc-600 max-w-2xl mb-6 text-sm">
          Inject viewer count + realistic chat bots into any live show. Bots
          use the stream&apos;s title + category context so lines stay on-topic.
          All actions are audit-logged.
        </p>

        {streams.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-2xl p-10 text-center text-zinc-500" data-testid="admin-boost-empty">
            <BroadcastIcon size={40} className="mx-auto text-zinc-300 mb-3" />
            No streams are live right now.
          </div>
        ) : (
          <div className="space-y-4">
            {streams.map((s) => (
              <StreamBoostCard key={s.id} stream={s} personas={personas} onChange={refresh} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StreamBoostCard({ stream, personas, onChange }) {
  const [viewers, setViewers] = useState(25);
  const [drainMinutes, setDrainMinutes] = useState(0);
  const [personaKey, setPersonaKey] = useState("");     // "" = Custom
  const [botCount, setBotCount] = useState(6);
  const [botRate, setBotRate] = useState(12);
  const [busy, setBusy] = useState(false);

  const activePreset = personaKey ? personas.find((p) => p.key === personaKey) : null;

  const injectViewers = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/streams/${stream.id}/boost/viewers`, {
        count: Number(viewers),
        drain_over_minutes: Number(drainMinutes) || 0,
      });
      toast.success(`Injected +${r.data.added} viewers (now ${r.data.viewer_count})`);
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startBots = async () => {
    setBusy(true);
    try {
      const payload = personaKey
        ? { persona: personaKey }
        : { count: Number(botCount), message_rate_seconds: Number(botRate) };
      const r = await api.post(`/admin/streams/${stream.id}/boost/bots/start`, payload);
      const label = r.data.persona
        ? `Started ${personas.find((p) => p.key === r.data.persona)?.label || r.data.persona}`
        : `${r.data.bots_added} bots live · every ~${r.data.message_rate_seconds}s`;
      toast.success(label);
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const stopBoost = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/streams/${stream.id}/boost/stop`);
      toast.success(`Stopped · cancelled ${r.data.cancelled_bot_tasks} bot tasks`);
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white border border-zinc-200 rounded-2xl p-5 md:p-6 shadow-sm"
      data-testid={`admin-boost-card-${stream.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
              <span className="h-1.5 w-1.5 rounded-full bg-red-300 animate-pulse" />
              Live
            </span>
            {stream.category && (
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                {stream.category}
              </span>
            )}
          </div>
          <Link
            to={`/stream/${stream.id}`}
            className="font-display font-black text-lg tracking-tight text-zinc-900 hover:underline block truncate"
            data-testid={`admin-boost-stream-title-${stream.id}`}
          >
            {stream.title || "Untitled stream"}
          </Link>
          <div className="text-xs text-zinc-500 mt-0.5 truncate">
            Host: {stream.seller_name || "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">
            Viewers
          </div>
          <div className="font-mono font-black text-2xl tabular-nums text-[#1D78AB]" data-testid={`admin-boost-viewer-count-${stream.id}`}>
            {stream.viewer_count || 0}
          </div>
          {stream.boost_active && (
            <div className="text-[10px] uppercase tracking-widest text-amber-600 font-mono mt-1">
              {stream.boost_bots_running} bots running
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 pt-5 border-t border-zinc-100">
        {/* Viewer boost */}
        <div className="space-y-2.5">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
            <UsersThreeIcon size={16} weight="fill" className="text-[#1D78AB]" />
            Inject viewers
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={2000}
              value={viewers}
              onChange={(e) => setViewers(e.target.value)}
              className="h-9 text-sm w-24"
              data-testid={`admin-boost-viewer-count-input-${stream.id}`}
            />
            <span className="text-xs text-zinc-500">viewers</span>
            <Input
              type="number"
              min={0}
              max={120}
              value={drainMinutes}
              onChange={(e) => setDrainMinutes(e.target.value)}
              className="h-9 text-sm w-20"
              placeholder="0"
              data-testid={`admin-boost-drain-input-${stream.id}`}
            />
            <span className="text-xs text-zinc-500">min drain</span>
          </div>
          <p className="text-[11px] text-zinc-400 leading-snug">
            Drain=0 keeps the count permanent for the show&apos;s duration. Otherwise
            it eases down gradually.
          </p>
          <Button
            disabled={busy}
            onClick={injectViewers}
            className="w-full bg-[#1D78AB] hover:bg-[#165d85] rounded-full h-9 text-sm"
            data-testid={`admin-boost-add-viewers-btn-${stream.id}`}
          >
            Add viewers
          </Button>
        </div>

        {/* Bots */}
        <div className="space-y-2.5">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
            <RobotIcon size={16} weight="fill" className="text-amber-600" />
            Realistic chat bots
          </div>

          {/* Persona picker — click a chip to lock the vibe. Empty selection
              falls back to manual count/rate below. */}
          <div className="flex items-center gap-1.5 flex-wrap" data-testid={`admin-boost-personas-${stream.id}`}>
            {personas.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPersonaKey(personaKey === p.key ? "" : p.key)}
                className={
                  "text-[11px] px-2.5 py-1 rounded-full border transition-colors " +
                  (personaKey === p.key
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-zinc-700 border-zinc-300 hover:border-amber-400")
                }
                title={p.description}
                data-testid={`admin-boost-persona-${p.key}-${stream.id}`}
                data-active={personaKey === p.key ? "1" : "0"}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPersonaKey("")}
              className={
                "text-[11px] px-2.5 py-1 rounded-full border transition-colors " +
                (personaKey === ""
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400")
              }
              data-testid={`admin-boost-persona-custom-${stream.id}`}
            >
              Custom
            </button>
          </div>

          {activePreset ? (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 leading-snug">
              <span className="font-semibold">{activePreset.label}</span> ·
              {" "}{activePreset.count} bots · every ~{activePreset.message_rate_seconds}s ·{" "}
              {activePreset.description}
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={40}
                  value={botCount}
                  onChange={(e) => setBotCount(e.target.value)}
                  className="h-9 text-sm w-20"
                  data-testid={`admin-boost-bot-count-input-${stream.id}`}
                />
                <span className="text-xs text-zinc-500">bots</span>
                <Input
                  type="number"
                  min={4}
                  max={120}
                  value={botRate}
                  onChange={(e) => setBotRate(e.target.value)}
                  className="h-9 text-sm w-20"
                  data-testid={`admin-boost-bot-rate-input-${stream.id}`}
                />
                <span className="text-xs text-zinc-500">sec/msg</span>
              </div>
              <p className="text-[11px] text-zinc-400 leading-snug">
                Each bot speaks every ~{botRate}s (±40% jitter) with an
                LLM-written line matching the show&apos;s title + category.
              </p>
            </>
          )}

          <Button
            disabled={busy}
            onClick={startBots}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full h-9 text-sm"
            data-testid={`admin-boost-start-bots-btn-${stream.id}`}
          >
            {stream.boost_active ? "Add more bots" : "Start bots"}
          </Button>
        </div>
      </div>

      {stream.boost_active && (
        <div className="mt-5 pt-4 border-t border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-zinc-500">
            <span className="font-semibold text-amber-600">Boost active</span>
            {" — "}
            {stream.boost_bots_running} bot{stream.boost_bots_running === 1 ? "" : "s"} running.
          </div>
          <Button
            variant="outline"
            disabled={busy}
            onClick={stopBoost}
            className="rounded-full h-9 text-sm border-red-300 text-red-700 hover:bg-red-50"
            data-testid={`admin-boost-stop-btn-${stream.id}`}
          >
            <StopCircleIcon size={16} weight="fill" className="mr-1" /> Stop boost
          </Button>
        </div>
      )}
    </div>
  );
}
